// app/shared/hooks/useErrorNotificationHook.ts
"use client";

import { useEffect } from 'react';

import { toast } from '@/components/ui/use-toast';

/**
 * Custom error interface that includes optional status code
 */
export interface CustomError extends Error {
  status?: number;
  code?: string;
  details?: any;
}

/**
 * Hook to handle error notifications
 * 
 * @param isError Boolean indicating if an error occurred
 * @param title Error title
 * @param error Error object
 * @param autoShowToast Whether to automatically show a toast notification
 */
export const useErrorNotificationHook = (
  isError: boolean, 
  title: string, 
  error: CustomError | null = null,
  autoShowToast: boolean = true
) => {
  useEffect(() => {
    if (isError && error && autoShowToast) {
      // Format error message
      const statusPrefix = error.status ? `[${error.status}] ` : '';
      const errorCode = error.code ? `Code: ${error.code}` : '';
      
      // Construct error description
      let description = error.message;
      if (errorCode) {
        description = `${errorCode}: ${description}`;
      }
      
      // Show error toast
      toast({
        title: `${statusPrefix}${title}`,
        description: description,
        variant: "destructive",
      });
    }
  }, [isError, title, error, autoShowToast]);
  
  // Return formatted error message for use in UI if needed
  return {
    formattedError: isError && error ? {
      title: title,
      message: error.message,
      status: error.status,
      code: error.code,
    } : null
  };
}; 